/**
 * T019 - US1: Provider Resources Grid Component
 * Feature 011 - Service Provider Profile Improvements
 *
 * Displays authored resources in a responsive grid layout
 * Shows resource cards with title, description, type badge, and thumbnail
 * Handles click navigation to resource detail page
 */

import { useNavigate } from 'react-router-dom';
import { FileText, Video, BookOpen, File } from 'lucide-react';
import type { Resource } from '../hooks/useProviderResources';

interface ProviderResourcesGridProps {
  resources: Resource[];
}

const getResourceIcon = (type: string | null | undefined) => {
  if (!type || typeof type !== 'string') {
    return File;
  }

  switch (type.toUpperCase()) {
    case 'ARTICLE':
      return FileText;
    case 'VIDEO':
      return Video;
    case 'GUIDE':
      return BookOpen;
    default:
      return File;
  }
};

const getResourceTypeBadgeColor = (type: string | null | undefined) => {
  if (!type || typeof type !== 'string') {
    return 'bg-gray-100 text-gray-700';
  }

  switch (type.toUpperCase()) {
    case 'ARTICLE':
      return 'bg-blue-100 text-blue-700';
    case 'VIDEO':
      return 'bg-purple-100 text-purple-700';
    case 'GUIDE':
      return 'bg-green-100 text-green-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

export const ProviderResourcesGrid = ({ resources }: ProviderResourcesGridProps) => {
  const navigate = useNavigate();

  if (resources.length === 0) {
    return (
      <div className="text-center py-12" data-testid="empty-state">
        <div className="mx-auto w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
          <FileText className="w-8 h-8 text-gray-400" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No resources created yet</h3>
        <p className="text-gray-600">
          This provider hasn't authored any educational resources at this time.
        </p>
      </div>
    );
  }

  return (
    <div
      className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
      data-testid="resources-grid"
    >
      {resources.map((resource) => {
        const typeName = resource.type?.name || 'Resource';
        const Icon = getResourceIcon(typeName);
        const badgeColor = getResourceTypeBadgeColor(typeName);
        const imageUrl = resource.image?.fileUrl;

        return (
          <div
            key={resource.id}
            data-testid={`resource-${resource.id}`}
            onClick={() => navigate(`/resource-hub-detail/${resource.seoUrl}`)}
            className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group"
          >
            {/* Thumbnail */}
            {imageUrl ? (
              <div className="aspect-video bg-gray-100 overflow-hidden">
                <img
                  src={imageUrl}
                  alt={resource.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
            ) : (
              <div className="aspect-video bg-gradient-to-br from-[#026857] to-[#025246] flex items-center justify-center">
                <Icon className="w-12 h-12 text-white opacity-50" />
              </div>
            )}

            {/* Content */}
            <div className="p-4">
              {/* Type Badge */}
              <div className="mb-2">
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badgeColor}`}>
                  <Icon className="w-3 h-3" />
                  {typeName}
                </span>
              </div>

              {/* Title */}
              <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-[#026857] transition-colors">
                {resource.title}
              </h3>

              {/* Excerpt/Description */}
              {resource.excerpt && (
                <p className="text-sm text-gray-600 line-clamp-3">
                  {resource.excerpt}
                </p>
              )}

              {/* Published Date */}
              <div className="mt-3 text-xs text-gray-500">
                {new Date(resource.publishedAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
